import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import type { UserRole } from '@makemypartner/shared';
import { supabase, isSupabaseConfigured } from './supabase';

export interface Profile {
  id: string;
  role: UserRole;
  display_name: string;
  avatar_url: string | null;
  phone_e164: string | null;
  city: string | null;
  languages: string[];
  is_banned: boolean;
  onboarded_at: string | null;
}

interface AuthState {
  session: Session | null;
  profile: Profile | null;
  /** True until the first session check completes. Guards must wait on this. */
  loading: boolean;
  isSupabaseConfigured: boolean;

  sendCode: (email: string, opts?: { createUser?: boolean; displayName?: string }) => Promise<void>;
  verifyCode: (email: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/** Errors we deliberately rewrite, because Supabase's wording leaks internals. */
function friendlyMessage(raw: string): string {
  const m = raw.toLowerCase();
  if (m.includes('signups not allowed') || m.includes('user not found')) {
    return 'No account found for that email. Create one instead.';
  }
  if (m.includes('token has expired') || m.includes('expired')) {
    return 'That code has expired. Ask for a new one.';
  }
  if (m.includes('invalid') && m.includes('token')) {
    return 'That code is not right. Check it and try again.';
  }
  if (m.includes('rate limit') || m.includes('too many')) {
    return 'Too many attempts. Wait a minute and try again.';
  }
  if (m.includes('failed to fetch')) {
    return 'Cannot reach the server. Check your connection.';
  }
  return raw;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select(
        'id, role, display_name, avatar_url, phone_e164, city, languages, is_banned, onboarded_at',
      )
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      // A missing profile is recoverable — the signup trigger may not have
      // committed yet. A permissions error is not, and should be visible.
      console.error('[auth] profile load failed:', error.message);
      setProfile(null);
      return;
    }
    setProfile(data as Profile | null);
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }

    let active = true;

    void supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return;
      setSession(data.session);
      if (data.session?.user) await loadProfile(data.session.user.id);
      if (active) setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      if (next?.user) {
        void loadProfile(next.user.id);
      } else {
        setProfile(null);
      }
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [loadProfile]);

  const sendCode = useCallback<AuthState['sendCode']>(async (email, opts) => {
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim().toLowerCase(),
      options: {
        // Login must NOT silently create an account — otherwise a typo in the
        // email address quietly makes a second, empty account.
        shouldCreateUser: opts?.createUser ?? false,
        data: opts?.displayName ? { display_name: opts.displayName.trim() } : undefined,
      },
    });
    if (error) throw new Error(friendlyMessage(error.message));
  }, []);

  const verifyCode = useCallback<AuthState['verifyCode']>(async (email, code) => {
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim().toLowerCase(),
      token: code.trim(),
      type: 'email',
    });
    if (error) throw new Error(friendlyMessage(error.message));
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (session?.user) await loadProfile(session.user.id);
  }, [session, loadProfile]);

  const value = useMemo<AuthState>(
    () => ({
      session,
      profile,
      loading,
      isSupabaseConfigured,
      sendCode,
      verifyCode,
      signOut,
      refreshProfile,
    }),
    [session, profile, loading, sendCode, verifyCode, signOut, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
